import { MapPin, BedDouble, Bath, Car, Ruler, ChevronLeft, ChevronRight, X, Share2, MessageCircle, Send, Check, Play, Calendar } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useState, useEffect } from "react";

const EleganteTemplate = ({ property, formData, setFormData, handleSubmit, submitting, openWhatsApp, handleShare, isDraftPreview, getImageUrl }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  
  const primaryColor = property.primary_color || "#a033fd";
  const allImages = [property.main_image, ...(property.images || [])].filter(Boolean);
  
  // Preload all images on mount so carousel transitions are instant
  useEffect(() => {
    allImages.forEach((src) => {
      const img = new Image();
      img.src = getImageUrl(src, { width: 1600 });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(price);
  };

  const handleContactClick = () => {
    if (property.contact_method === "form") {
      document.getElementById('elegante-contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      openWhatsApp();
    }
  };

  const specs = [
    property.specs?.bedrooms > 0 && { label: "Recámaras", value: property.specs.bedrooms, icon: BedDouble },
    property.specs?.bathrooms > 0 && { label: "Baños", value: property.specs.bathrooms, icon: Bath },
    property.specs?.half_bathrooms > 0 && { label: "Medio Baño", value: property.specs.half_bathrooms, icon: Bath },
    property.specs?.parking > 0 && { label: "Estacionamiento", value: property.specs.parking, icon: Car },
    property.specs?.construction_meters > 0 && { label: "Construcción", value: `${property.specs.construction_meters}m²`, icon: Ruler },
    property.specs?.square_meters > 0 && { label: "Terreno", value: `${property.specs.square_meters}m²`, icon: Ruler },
  ].filter(Boolean);

  const extraSpaces = [
    property.specs?.tv_room && "Estar de TV",
    property.specs?.laundry_room && "Cuarto de Lavado",
    property.specs?.service_room && "Cuarto de Servicio",
    property.specs?.roof_garden && "Roof Garden",
    property.specs?.balcony && "Balcón",
    property.specs?.terrace && "Terraza",
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Draft Banner */}
      {isDraftPreview && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium shadow-lg">
          Vista previa — Esta propiedad no es visible al público.{" "}
          <a href="/precios" className="underline font-bold">Activa tu plan para publicarla</a>
        </div>
      )}

      {/* Hero - Fullscreen Image with Overlay */}
      <section className="relative h-screen overflow-hidden">
        {allImages.map((src, i) => (
          <img
            key={i}
            src={getImageUrl(src, { width: 1600 })}
            alt={property.title}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${currentImage === i ? 'opacity-100' : 'opacity-0'}`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
        
        {/* Nav Arrows on Hero */}
        {allImages.length > 1 && (
          <>
            <button onClick={() => setCurrentImage(i => (i - 1 + allImages.length) % allImages.length)}
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors z-10 border border-white/20">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button onClick={() => setCurrentImage(i => (i + 1) % allImages.length)}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors z-10 border border-white/20">
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}
        
        {/* Image counter */}
        {allImages.length > 1 && (
          <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm text-white/80 px-3 py-1.5 rounded-full text-sm z-10 cursor-pointer border border-white/10" onClick={() => setShowGallery(true)}>
            {currentImage + 1} / {allImages.length} — Ver galería
          </div>
        )}
        
        {/* Top Actions */}
        <div className="absolute top-6 left-6 z-10 flex gap-2">
          <Button variant="outline" size="sm" onClick={handleShare} className="bg-black/30 backdrop-blur-sm border-white/20 text-white hover:bg-black/50 hover:text-white">
            <Share2 className="w-4 h-4 mr-1" /> Compartir
          </Button>
        </div>
        
        {/* Hero Content - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-12 lg:p-16">
          <div className="max-w-5xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ background: primaryColor }}>
                {property.operation_type === "renta" ? "En Renta" : "En Venta"}
              </span>
              <span className="px-3 py-1 rounded-full text-xs bg-white/10 backdrop-blur-sm border border-white/20 capitalize">
                {property.property_type}
              </span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-3 leading-tight">{property.title}</h1>
            <p className="text-white/60 flex items-center gap-2 text-lg mb-4">
              <MapPin className="w-5 h-5" /> {property.location}
            </p>
            <p className="text-4xl sm:text-5xl font-bold" style={{ color: primaryColor }}>{formatPrice(property.price)}</p>
          </div>
        </div>
      </section>

      {/* Specs Bar */}
      {specs.length > 0 && (
        <section className="border-y border-white/10 bg-gray-900/50 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <div className={`grid gap-4 sm:gap-6 ${
              specs.length === 1 ? 'grid-cols-1' :
              specs.length === 2 ? 'grid-cols-2' :
              specs.length === 3 ? 'grid-cols-3' :
              specs.length === 4 ? 'grid-cols-3 sm:grid-cols-4' :
              specs.length === 5 ? 'grid-cols-3 sm:grid-cols-5' :
              'grid-cols-3 sm:grid-cols-6'
            }`}>
              {specs.map((spec, i) => (
                <div key={i} className="flex flex-col sm:flex-row items-center sm:items-center justify-center gap-1.5 sm:gap-3 text-center sm:text-left">
                  <spec.icon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: primaryColor }} />
                  <div>
                    <p className="text-lg sm:text-2xl font-bold leading-tight">{spec.value}</p>
                    <p className="text-[10px] sm:text-xs text-white/50 uppercase tracking-wider leading-tight">{spec.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-16">
          {/* Description + Amenities */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Acerca de esta propiedad</h2>
              <p className="text-white/70 leading-relaxed text-lg whitespace-pre-line">{property.description}</p>
            </div>

            {/* Espacios Adicionales */}
            {extraSpaces.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Espacios Adicionales</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {extraSpaces.map((space, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl p-3 border border-white/5">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
                      <span className="text-white/80 text-sm">{space}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Amenidades</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {property.amenities.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl p-3 border border-white/5">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
                      <span className="text-white/80 text-sm">{a.label || a.name || a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Video */}
            {property.video_embed_url && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Video</h2>
                <div className="aspect-video rounded-2xl overflow-hidden border border-white/10">
                  <iframe src={property.video_embed_url} title="Video" className="w-full h-full" allowFullScreen />
                </div>
              </div>
            )}

            {/* Map */}
            {property.latitude && property.longitude && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Ubicación</h2>
                <div className="h-[400px] rounded-2xl overflow-hidden border border-white/10">
                  <iframe
                    title="Ubicación"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${property.longitude-0.005},${property.latitude-0.005},${property.longitude+0.005},${property.latitude+0.005}&layer=mapnik&marker=${property.latitude},${property.longitude}`}
                    className="w-full h-full border-0"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Contact */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {/* WhatsApp-only CTA */}
              {property.contact_method === "whatsapp" && (
                <div id="elegante-contact-form" className="rounded-2xl p-6 border border-white/10 backdrop-blur-sm scroll-mt-24" style={{ background: `linear-gradient(135deg, ${primaryColor}33, transparent)` }}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: primaryColor }}>
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-xl text-white">{property.form_title || "Agenda una visita"}</h3>
                      <p className="text-sm text-white/60 mt-0.5">{property.form_description || "Conócela en persona. Respondemos al instante."}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-5 text-sm text-white/80">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} /> Respuesta en minutos</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} /> Sin compromiso</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} /> Resolvemos tus dudas</li>
                  </ul>
                  <Button onClick={openWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold" data-testid="whatsapp-cta-elegante">
                    <MessageCircle className="w-5 h-5 mr-2" /> Agendar por WhatsApp
                  </Button>
                </div>
              )}

              {/* WhatsApp button (when both available) */}
              {property.contact_method === "both" && (
                <Button onClick={openWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white mb-4 h-12 text-base">
                  <MessageCircle className="w-5 h-5 mr-2" /> Contactar por WhatsApp
                </Button>
              )}
              
              {/* Contact Form */}
              {property.contact_method !== "whatsapp" && (
                <div id="elegante-contact-form" className="rounded-2xl p-6 bg-white/5 border border-white/10 backdrop-blur-sm scroll-mt-24">
                  <h3 className="font-bold text-xl mb-1">{property.form_title || "Solicitar información"}</h3>
                  <p className="text-white/50 text-sm mb-5">{property.form_description || "Te contactaremos a la brevedad"}</p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <Input
                      placeholder="Tu nombre completo"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                    <Input
                      type="tel"
                      placeholder="WhatsApp"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                    <Input
                      placeholder="Mensaje (opcional)"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                    <Button type="submit" className="w-full text-white h-11" style={{ background: primaryColor }} disabled={submitting}>
                      {submitting ? "Enviando..." : "Enviar Mensaje"}
                      <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Gallery */}
      {showGallery && (
        <div className="fixed inset-0 z-[70] bg-black flex items-center justify-center" onClick={() => setShowGallery(false)}>
          <button onClick={() => setShowGallery(false)} className="absolute top-4 right-4 text-white/60 hover:text-white z-10"><X className="w-8 h-8" /></button>
          <button onClick={(e) => { e.stopPropagation(); setCurrentImage(i => (i - 1 + allImages.length) % allImages.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white z-10"><ChevronLeft className="w-10 h-10" /></button>
          <button onClick={(e) => { e.stopPropagation(); setCurrentImage(i => (i + 1) % allImages.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white z-10"><ChevronRight className="w-10 h-10" /></button>
          <img src={getImageUrl(allImages[currentImage], { width: 1600 })} alt="" className="max-h-[90vh] max-w-[95vw] object-contain" onClick={e => e.stopPropagation()} />
          <div className="absolute bottom-6 text-white/40 text-sm">{currentImage + 1} / {allImages.length}</div>
        </div>
      )}

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-950/95 backdrop-blur-md border-t border-white/10 px-3 py-2.5 flex items-center justify-between gap-2 z-50">
        <p className="text-xl font-bold truncate min-w-0" style={{ color: primaryColor }}>{formatPrice(property.price)}</p>
        <Button
          size="sm"
          onClick={handleContactClick}
          className={`flex-shrink-0 text-white ${property.contact_method !== "form" ? "bg-green-600 hover:bg-green-700" : ""}`}
          style={property.contact_method === "form" ? { background: primaryColor } : {}}
          data-testid="elegante-mobile-contact-btn"
        >
          {property.contact_method !== "form" ? (
            <><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</>
          ) : (
            <><Send className="w-4 h-4 mr-1.5" /> Contactar</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EleganteTemplate;
